import { FC, Fragment, useMemo } from 'react';
import moment from 'moment';

import {
  type SearchParamsPetCounts,
  SearchParamsPetType,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { useApiServiceHolidayRateCheckRetrieve } from '@rover/rsdk/src/apiClient/latest/api-service-holiday-rate-check-retrieve';
import type { ApiServiceHolidayRateCheckRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiServiceHolidayRateCheckRetrieveParams';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import type { EnhancedPersonSitterProfileServiceAddOn } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import { ServiceAddOnTypeSlugs as AddOnSlugs } from '@rover/types/src/ServiceAddOn';

import { selectRateForPetType, usePrimaryPetType } from '../../utils/utils';

import AcceptingNewCustomersLabel from './AcceptingNewCustomersLabel';
import Separator from './Separator';
import ServiceHeader from './ServiceHeader';
import ServiceHeaderLoadingShimmer from './ServiceHeaderLoadingShimmer';

export type ServiceSectionItemProps = {
  index: number;
  service: EnhancedPersonSitterProfileService;
  sitterProfileOpk: string;
  petType?: SearchParamsPetType;
  petCounts: SearchParamsPetCounts;
  selectedDate?: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
  showSittersRates?: boolean;
};

const ServiceSectionItem: FC<ServiceSectionItemProps> = ({
  index,
  service,
  sitterProfileOpk,
  petType,
  petCounts,
  selectedDate,
  selectedEndDate,
  hasDateRange = false,
  showSittersRates = true,
}) => {
  const { serviceAddOns, slug } = service;
  const primaryPetType = usePrimaryPetType(petCounts, petType, [service]);

  const selectedDates: ApiServiceHolidayRateCheckRetrieveParams = {
    ...(selectedDate ? { startDate: moment(selectedDate).format('YYYY-MM-DD') } : {}),
    ...(selectedEndDate ? { endDate: moment(selectedEndDate).format('YYYY-MM-DD') } : {}),
  };
  const { data: holidayRateCheck, isLoading: isLoadingHolidayRate } =
    useApiServiceHolidayRateCheckRetrieve(sitterProfileOpk, slug, selectedDates);
  const isHolidayRate = !!holidayRateCheck?.holidayRate;

  const rateData = useMemo((): EnhancedPersonSitterProfileServiceAddOn | undefined => {
    const holidayRate = serviceAddOns.find((sa) => sa.slug === AddOnSlugs.holidayRate);
    if (isHolidayRate && holidayRate && hasDateRange) {
      return holidayRate;
    }

    return selectRateForPetType(serviceAddOns, primaryPetType);
  }, [primaryPetType, hasDateRange, isHolidayRate, serviceAddOns]);

  return (
    <Fragment>
      <Separator withBorder={index !== 0} />
      {isLoadingHolidayRate ? (
        <ServiceHeaderLoadingShimmer />
      ) : (
        <>
          <ServiceHeader
            service={service}
            standardRate={rateData}
            priceInCredits={rateData?.priceInCredits}
            showSittersRates={showSittersRates}
          />
          <AcceptingNewCustomersLabel service={service} />
        </>
      )}
    </Fragment>
  );
};

export default ServiceSectionItem;
